import type { Board } from "../board";
import { CollectableItem, type CollectablePickupResult } from "../collectableItem";
import { Event } from "../events/event";
import { EventParameter } from "../events/eventParameter";
import { EventType } from "../events/eventType";
import type { Player } from "../player";
import { getWeaponClass } from "../serialization";
import { Strings } from "../strings";
import { getFamilyForWeapon } from "../weaponFamilyConfig";

export class Ammo extends CollectableItem {
	static key = "Ammo";
	weaponKey: string;

	constructor(weaponKey: string, count: number) {
		super(count);
		this.weaponKey = weaponKey;
	}

	getWeaponNameKey(): string {
		return "wname_" + this.weaponKey;
	}

	getName(): string {
		const translated = Strings.get(this.getWeaponNameKey());
		if (translated === null) return this.weaponKey;
		return translated;
	}

	pickupBy(player: Player, board: Board): CollectablePickupResult {
		const cls = getWeaponClass(this.weaponKey);
		const existing = player.arsenal.getAllWeapons().find((w) => w instanceof cls);

		let isInfinite = false;
		let newlyAcquiredWeapon = false;
		if (existing) {
			isInfinite = existing.infinite;
			if (!existing.infinite) existing.ammo += this.count;
		} else {
			const weapon = new cls(player);
			weapon.ammo = this.count;
			isInfinite = weapon.infinite;
			player.arsenal.addWeapon(weapon);
			newlyAcquiredWeapon = true;
		}

		board.stats.recordAmmoCollected(this.weaponKey, isInfinite ? 0 : this.count);

		if (isInfinite) {
			const event = new Event(EventType.GOT_WEAPON);
			event.setParameter(EventParameter.WEAPON_NAME, this.getWeaponNameKey());
			board.addEvent(event);
		} else {
			const event = new Event(EventType.GOT_AMMO);
			event.setParameter(EventParameter.AMMO_AMOUNT, String(this.count));
			event.setParameter(EventParameter.AMMO_NAME, this.getWeaponNameKey());
			board.addEvent(event);
		}

		if (newlyAcquiredWeapon && board.game?.markWeaponHintShown(this.weaponKey)) {
			const acquired = player.arsenal.getAllWeapons().find((w) => w instanceof cls);
			if (acquired) {
				const familyId = getFamilyForWeapon(acquired);
				const hintEvent = new Event(EventType.FIRST_WEAPON_PICKUP);
				hintEvent.setParameter(EventParameter.WEAPON_NAME, this.getWeaponNameKey());
				hintEvent.setParameter(EventParameter.FAMILY_HOTKEY, String(familyId));
				board.addEvent(hintEvent);
			}
		}

		return { pickupKey: this.weaponKey, amount: this.count, isInfinite };
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), weaponKey: this.weaponKey };
	}

	static deserialize(data: any): Ammo {
		return new Ammo(data.weaponKey, data.count);
	}
}
